import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, CheckCircle2, ArrowRight } from 'lucide-react';
import { FALLBACK_PAGES } from '../data';
import GalleryModal from '../components/GalleryModal';

export default function Safety() {
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    async function fetchPage() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/by-slug/da/risk-functional-safety');
        if (res.ok) {
          const data = await res.json();
          setPageData(data);
          return;
        }
      } catch (err) {
        console.error('Error fetching safety page:', err);
      }
      setPageData(FALLBACK_PAGES['risk-functional-safety']);
    }
    fetchPage();
  }, []);

  const title = pageData?.title?.da || pageData?.title || "Risk & Functional Safety - CARPA ApS";
  const desc = pageData?.meta_description?.da || pageData?.meta_description || "Specialiseret rådgivning inden for funktionel sikkerhed, SIL, LOPA, SIF/SIS og Cause & Effect reviews i procesindustrien.";

  const safetyAreas = [
    {
      title: 'SIL Assessments (Safety Integrity Level)',
      desc: 'Vi faciliterer og dokumenterer SIL-klassificeringer for at bestemme de nødvendige sikkerhedsniveauer for jeres instrumenterede sikkerhedsfunktioner (SIF). Vi sikrer, at jeres anlæg overholder standarderne IEC 61508 og IEC 61511.'
    },
    {
      title: 'LOPA (Layer of Protection Analysis)',
      desc: 'Vi udfører LOPA-analyser som en semi-kvantitativ metode til at vurdere risikoen og bestemme, om de eksisterende uafhængige beskyttelseslag er tilstrækkelige til at bringe risikoen ned på et acceptabelt niveau.'
    },
    {
      title: 'SIF/SIS Review',
      desc: 'Vi gennemgår jeres Safety Instrumented Systems (SIS) og tilhørende funktioner (SIF) for at verificere, at designet opfylder de definerede SIL-krav, herunder beregning af PFD (Probability of Failure on Demand).'
    },
    {
      title: 'Cause & Effect Review',
      desc: 'Vi analyserer og verificerer Cause & Effect-matricer for at sikre, at anlæggets nødstop- og sikkerhedssystemer reagerer korrekt og koordineret på alle tænkelige hændelser.'
    },
    {
      title: 'Functional Safety Lifecycle Support',
      desc: 'Vi støtter jer gennem hele sikkerhedslivscyklussen – fra de indledende risikovurderinger og specifikationer til idriftsættelse, drift, vedligeholdelse og eventuel modifikation af sikkerhedssystemerne.'
    },
    {
      title: 'Risk Workshops',
      desc: 'Vi faciliterer HAZOP, HAZID og andre risikovurderings-workshops med deltagelse af jeres tekniske eksperter for at identificere, vurdere og reducere risici tidligt i projektet.'
    },
    {
      title: 'Technical Safety Coordination',
      desc: 'Vi koordinerer alle sikkerhedstekniske aktiviteter i projektet og sikrer, at leverandører og underentreprenører leverer den nødvendige dokumentation og overholder sikkerhedskravene.'
    }
  ];

  return (
    <div className="bg-[#f7f8fa] text-[#0e1f2f] min-h-screen">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[55vh] lg:min-h-[60vh] flex flex-col justify-center py-16 md:py-24 overflow-hidden bg-[#0a1520] border-b border-[#0e1f2f]/20 mb-16">
        {/* Background Grid Pattern & Image Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/59acd1b6-4973-4db5-aba9-2022467ddb50/a29dceec-70eb-4c22-a9d9-3c53691991fe.jpeg"
            alt="Risk & Functional Safety"
            className="h-full w-full object-cover opacity-35 lg:opacity-45 object-center"
          />
          {/* Subtle Technical Grid Overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(23,211,214,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(23,211,214,0.04)_1px,transparent_1px)] bg-[size:24px_24px]" />
          {/* Gradient Overlay preserving text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1520] via-[#0a1520]/95 to-transparent hidden lg:block" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1520]/90 via-[#0a1520]/80 to-[#0a1520] lg:hidden" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Title & Process */}
            <div className="lg:col-span-7 space-y-6 text-left">
              {/* Action/Process Header Link */}
              <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm font-mono tracking-wider uppercase">
                <span className="text-[#17d3d6] font-bold">
                  {pageData?.hero_badge?.da || pageData?.hero_badge || "Identificer → Vurder → Reducer Risiko"}
                </span>
              </div>

              <div className="space-y-2">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-wide uppercase text-white leading-none">
                  {pageData?.hero_title?.da || pageData?.hero_title || "Risk & Safety"}
                </h1>
                <div className="w-16 h-[3px] bg-[#17d3d6] mt-4" />
              </div>

              <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl">
                {pageData?.hero_description?.da || pageData?.hero_description || "Inden for PtX-, energi- og procesindustrien er sikkerhed og pålidelighed helt afgørende. CARPA tilbyder specialistrådgivning inden for funktionel sikkerhed med fokus på praktisk anvendelighed, tillid, struktur og fuld overholdelse af gældende standarder."}
              </p>
            </div>

            {/* Right Column: Premium Floating Card with Glow Border */}
            <div className="lg:col-span-5">
              <div className="bg-[#041326]/65 backdrop-blur-md border border-[rgba(23,211,214,0.15)] rounded-xl p-6 lg:p-8 flex flex-col justify-between transition-all duration-300 hover:border-[#17d3d6]/40 shadow-2xl">
                <div>
                  <div className="flex items-center gap-4 pb-4 border-b border-slate-700/40">
                    <div className="w-12 h-12 shrink-0 rounded-full border border-[#17d3d6] flex items-center justify-center text-[#17d3d6]">
                      <Shield className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-white text-lg font-bold leading-tight">Sikkerhed i fokus</h3>
                      <p className="text-xs text-slate-400 font-mono tracking-wider uppercase mt-0.5">CARPA ApS</p>
                    </div>
                  </div>

                  <ul className="mt-6 space-y-3.5">
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      IEC 61508 & IEC 61511 compliance
                    </li>
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Erfarne TÜV-certificerede rådgivere
                    </li>
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      HAZOP, LOPA & SIL-analyser
                    </li>
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Praktisk risikostyring & workshops
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6">

        {/* Specialist Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left: Key message & trust */}
          <div className="lg:col-span-4 space-y-8 lg:sticky lg:top-28">
            <div className="rounded-xl border border-[#0e1f2f]/10 bg-white p-6 md:p-8 space-y-4 shadow-sm">
              <div className="inline-flex rounded-lg bg-[#3c8888]/10 p-2.5 text-[#3c8888] border border-[#3c8888]/20">
                <Shield className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-bold text-[#0e1f2f]">Faglig tyngde & Tillid</h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Funktionel sikkerhed handler ikke kun om beregninger, men om at skabe en dyb forståelse af anlæggets risici og sikre, at de tekniske løsninger are praktisk anvendelige for driftsorganisationen.
              </p>
              <div className="border-t border-[#0e1f2f]/10 pt-4 space-y-3 text-xs font-mono text-stone-600">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#3c8888]" />
                  <span>IEC 61508 & IEC 61511 compliance</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#3c8888]" />
                  <span>Erfarne TÜV-certificerede rådgivere</span>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-[#0e1f2f]/10 bg-white p-6 space-y-3 shadow-sm">
              <h4 className="font-mono text-xs font-bold uppercase tracking-wider text-[#0e1f2f]">Har du brug for en safety review?</h4>
              <p className="text-xs text-stone-600 leading-relaxed">
                Vi faciliterer jeres næste HAZOP eller SIL-workshop med fokus på en effektiv og struktureret proces.
              </p>
              <a
                href="mailto:llk@carpa.dk"
                className="inline-flex items-center gap-1 text-xs font-bold text-[#3c8888] hover:text-[#3c8888]/80 transition-colors"
              >
                <span>Kontakt os om safety</span>
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>

          {/* Right: Specialist areas */}
          <div className="lg:col-span-8 space-y-8">
            <div className="grid grid-cols-1 gap-6">
              {safetyAreas.map((area, idx) => (
                <div
                  key={idx}
                  className="rounded-lg border border-[#0e1f2f]/10 bg-white p-6 md:p-8 hover:border-[#3c8888]/30 hover:shadow-sm transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#3c8888]/10 text-[#3c8888] border border-[#3c8888]/20 text-xs font-mono font-bold">
                      0{idx + 1}
                    </span>
                    <div className="space-y-2">
                      <h3 className="text-lg font-bold text-[#0e1f2f] tracking-tight">
                        {area.title}
                      </h3>
                      <p className="text-sm text-stone-600 leading-relaxed">
                        {area.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Gallery display */}
        {pageData?.gallery_slug && (
          <GalleryModal gallerySlug={pageData.gallery_slug} />
        )}
      </div>
    </div>
  );
}